import type { QueryClient } from '@tanstack/react-query';

/**
 * The query keys a change to one request makes stale.
 *
 * Its own module so the mutation hooks in `useSupportRequests` and the event
 * stream in `SupportRequestStreamContext` share one list rather than each
 * keeping their own copy — the stream's whole design is that it says "this
 * request changed" and lets the existing queries refetch, so the two must not
 * drift.
 */
export function invalidateRequestViews(queryClient: QueryClient, requestId: string) {
  // A message write can change all of these: the thread, any open thread panel,
  // the artifact and attachment lists (a detach can remove one), and the request
  // itself (the denormalized last-message fields are recomputed server-side).
  queryClient.invalidateQueries({ queryKey: ['support-request-messages', requestId] });
  queryClient.invalidateQueries({ queryKey: ['support-request-thread', requestId] });
  queryClient.invalidateQueries({ queryKey: ['support-request-attachments', requestId] });
  queryClient.invalidateQueries({ queryKey: ['support-request-artifacts', requestId] });
  queryClient.invalidateQueries({ queryKey: ['support-request', requestId] });
  queryClient.invalidateQueries({ queryKey: ['support-requests'] });
}

/**
 * Same, plus the sidebar badge.
 *
 * Split from `invalidateRequestViews` because the badge counts messages from
 * *other* people: our own write can't change it, so the mutation hooks skip it,
 * while every stream event is by definition somebody else's change.
 */
export function invalidateRequestViewsAndBadge(queryClient: QueryClient, requestId: string) {
  invalidateRequestViews(queryClient, requestId);
  queryClient.invalidateQueries({ queryKey: ['support-requests-unread'] });
}

/**
 * Refetch every support-request view.
 *
 * This is what closes the gap over a reconnect: while the stream was down a
 * reply could have landed, and the server deliberately doesn't replay history
 * on connect. One sweep on connect is cheaper than any catch-up protocol, and
 * it can't miss anything.
 */
export function invalidateAllRequestViews(queryClient: QueryClient) {
  for (const key of [
    'support-requests',
    'support-request',
    'support-request-messages',
    'support-request-thread',
    'support-request-attachments',
    'support-request-artifacts',
    'support-requests-unread',
  ]) {
    queryClient.invalidateQueries({ queryKey: [key] });
  }
}
